import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './cn';
import { PencilSimple } from './icons';

interface Props {
  value: string;
  /** Grava o valor novo. Se a promessa falhar, o anterior volta e o erro aparece. */
  onSave: (next: string) => Promise<unknown>;
  /** Nome do campo para leitores de tela e para o lápis: "título do curso". */
  label: string;
  /** Texto quando vazio, só para quem edita: "Adicionar descrição". */
  placeholder?: string;
  /** Várias linhas (descrição): Enter salva, Shift+Enter quebra a linha. */
  multiline?: boolean;
  maxLength?: number;
  /** Arruma o texto antes de comparar e salvar. Padrão: tira os espaços das pontas. */
  normalize?: (v: string) => string;
  /** Por que o valor não serve, ou `null`. Inválido não sai do navegador. */
  validate?: (v: string) => string | null;
  /** A mensagem quando a gravação falha. */
  describeError?: (e: unknown) => string;
  /** Só leitura: mostra o texto, sem lápis. */
  readOnly?: boolean;
  /** Tipografia comum ao texto e ao campo — o campo abre com a mesma cara. */
  className?: string;
  /** Conteúdo próprio no modo leitura (ex.: as tags como etiquetas). */
  display?: ReactNode;
  /** `pencil`: só o lápis abre o campo — quando o texto já é um link. */
  trigger?: 'text' | 'pencil';
}

const keep = (v: string) => v.trim();
const failed = () => 'Não foi possível salvar. Tente de novo.';

/**
 * Texto editável no lugar. Clicar no texto (ou no lápis) abre um campo com a
 * mesma tipografia, sem borda, num tom acima do fundo; Enter ou sair do campo
 * salva, Esc desiste.
 *
 * Otimista: o valor novo aparece na hora, enquanto a API grava. Se ela
 * recusar, o anterior volta e a mensagem fica logo abaixo.
 */
export function InlineText({
  value,
  onSave,
  label,
  placeholder,
  multiline,
  maxLength,
  normalize = keep,
  validate,
  describeError = failed,
  readOnly,
  className,
  display,
  trigger = 'text',
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const pencil = useRef<HTMLButtonElement>(null);
  // Enter e Esc fecham o campo; o blur que vem junto não pode salvar de novo.
  const settled = useRef(false);
  const refocus = useRef(false);

  useEffect(() => {
    if (editing) {
      const el = field.current;
      el?.focus();
      el?.setSelectionRange(el.value.length, el.value.length);
    } else if (refocus.current) {
      refocus.current = false;
      pencil.current?.focus();
    }
  }, [editing]);

  const shown = pending ?? value;

  if (readOnly) {
    if (display) return <>{display}</>;
    return shown ? <span className={cn('block', className)}>{shown}</span> : null;
  }

  const start = () => {
    settled.current = false;
    setDraft(shown);
    setError(null);
    setEditing(true);
  };

  const close = (viaKeyboard: boolean) => {
    settled.current = true;
    refocus.current = viaKeyboard;
    setEditing(false);
  };

  const commit = async (viaKeyboard: boolean) => {
    if (settled.current) return;
    const next = normalize(draft);
    if (next === value) {
      close(viaKeyboard);
      return;
    }
    const problem = validate?.(next) ?? null;
    if (problem) {
      // Fica no campo, com o motivo, até corrigir ou desistir (Esc).
      setError(problem);
      return;
    }
    close(viaKeyboard);
    setPending(next);
    try {
      await onSave(next);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setPending(null);
    }
  };

  const cancel = () => {
    close(true);
    setDraft(value);
    setError(null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      // O Esc fecha só o campo, não o modal ou o menu em volta.
      e.stopPropagation();
      cancel();
    } else if (e.key === 'Enter' && !(multiline && e.shiftKey)) {
      e.preventDefault();
      void commit(true);
    }
  };

  const alert = error && (
    <span role="alert" className="mt-1 block text-xs font-normal text-fg">
      {error}
    </span>
  );

  if (editing) {
    const fieldClass = cn(
      'block w-full rounded-md bg-surface-2 px-1.5 -mx-1.5 text-fg outline-none ring-2 ring-teal-300',
      className,
    );
    const props = {
      ref: field,
      value: draft,
      maxLength,
      'aria-label': label,
      'aria-invalid': error ? true : undefined,
      onChange: (e: { target: { value: string } }) => {
        setDraft(e.target.value);
        if (error) setError(null);
      },
      onKeyDown,
      onBlur: () => void commit(false),
    };
    return (
      <span className="block min-w-0">
        {multiline ? (
          <textarea
            {...props}
            rows={3}
            className={cn(fieldClass, 'field-sizing-content min-h-16 resize-none')}
          />
        ) : (
          <input {...props} type="text" className={fieldClass} />
        )}
        {alert}
      </span>
    );
  }

  return (
    <span className="block min-w-0">
      <span className="group/inline flex min-w-0 items-start gap-1">
        <span
          onClick={trigger === 'text' ? start : undefined}
          className={cn(
            'block min-w-0 rounded-md px-1.5 -mx-1.5 transition-colors',
            trigger === 'text' && 'cursor-text hover:bg-surface-2',
            pending !== null && 'opacity-70',
            !shown && !display && 'text-fg-subtle',
            className,
          )}
        >
          {display ?? (shown || placeholder)}
        </span>
        <button
          ref={pencil}
          type="button"
          onClick={start}
          aria-label={`Editar ${label}`}
          title="Editar"
          className="mt-0.5 shrink-0 rounded-md p-1 text-fg-subtle opacity-0 transition-opacity group-hover/inline:opacity-100 hover:bg-surface-2 hover:text-fg focus-visible:opacity-100"
        >
          <PencilSimple aria-hidden className="size-3.5" />
        </button>
      </span>
      {alert}
    </span>
  );
}
